/**
 * Incidents Layer
 * Data-driven layer for displaying incident locations
 *
 * Uses centralized ImagePreloader / IconRegistry to ensure each icon
 * is loaded at most once per session.
 * Uses ensureGeoJsonLayer for deterministic, idempotent layer management.
 */

import { ENDPOINTS } from '@/feat/api-service-layer/endpoints';
import { BASE_URL } from '@/feat/api-service-layer/fetchClient';
import { removeGeoJsonLayer, waitForStyleLoaded } from '../geoJsonLayerHelper';
import { imagePreloader } from '../ImagePreloader';

const SOURCE_ID = 'Incidents';
const LAYER_ID = 'Incidents Layer';

export interface Incident {
  longitude: number;
  latitude: number;
  name?: string;
  [key: string]: unknown;
}

/**
 * Fetch incidents from API
 * @param getToken - Function to get authentication token
 * @returns Promise resolving to array of incidents
 */
export async function fetchIncidents(getToken: () => Promise<string>): Promise<Incident[]> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${ENDPOINTS.incidents}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) throw new Error(`Failed to fetch incidents: ${res.status}`);
  const data = await res.json();
  return data.incidents || data.results || data.result || (Array.isArray(data) ? data : []);
}

/**
 * Build GeoJSON FeatureCollection from incidents array
 */
function buildGeoJson(incidents: Incident[]): GeoJSON.FeatureCollection {
  const validIncidents = incidents.filter(
    (inc) =>
      typeof inc.longitude === 'number' &&
      typeof inc.latitude === 'number' &&
      !Number.isNaN(inc.longitude) &&
      !Number.isNaN(inc.latitude),
  );
  return {
    type: 'FeatureCollection',
    features: validIncidents.map((incident) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [incident.longitude, incident.latitude],
      },
      properties: incident as GeoJSON.GeoJsonProperties,
    })),
  };
}

/**
 * Add Incidents layer from pre-fetched data
 * @param map - Mapbox map instance
 * @param incidents - Array of incident objects
 */
export async function addIncidentsLayer(map: mapboxgl.Map | null, incidents: Incident[]): Promise<void> {
  if (!map) return;

  // Wait for style to be loaded
  const styleReady = await waitForStyleLoaded(map);
  if (!styleReady) {
    console.warn('Incidents: style not ready, skipping');
    return;
  }

  // Build data
  const geojson = buildGeoJson(incidents);

  // If no valid incidents, clear layer
  if (geojson.features.length === 0) {
    removeGeoJsonLayer(map, LAYER_ID, SOURCE_ID);
    // eslint-disable-next-line no-console
    // No valid incident data; layer cleared.
    return;
  }

  // Try to load icon
  let useIcon = false;
  const iconDef = imagePreloader.getIconDefinition('incident:default');
  const incidentsIconId = iconDef?.id || 'incident-icon';

  try {
    const loadedId = await imagePreloader.ensureIconLoaded(map, 'incident:default');
    useIcon = !!loadedId;
  } catch {
    useIcon = false;
  }

  // Re-check style after async icon loading
  if (!map.isStyleLoaded()) {
    const ready = await waitForStyleLoaded(map);
    if (!ready) {
      console.warn('Incidents: style not ready after icon load, skipping');
      return;
    }
  }

  try {
    // Remove existing layer/source if present
    if (map.getLayer(LAYER_ID)) {
      map.removeLayer(LAYER_ID);
    }
    if (map.getSource(SOURCE_ID)) {
      map.removeSource(SOURCE_ID);
    }

    // Add source
    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: geojson,
    });

    // Add layer with icon or circle fallback
    if (useIcon) {
      map.addLayer({
        id: LAYER_ID,
        type: 'symbol',
        source: SOURCE_ID,
        layout: {
          'icon-image': incidentsIconId,
          'icon-size': 0.02,
          'icon-allow-overlap': false,
          'icon-ignore-placement': false,
          visibility: 'none',
        },
      });
      // eslint-disable-next-line no-console
      // Rendering icon-based incident features.
    } else {
      map.addLayer({
        id: LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        paint: {
          'circle-radius': 5,
          'circle-color': '#f97316',
          'circle-stroke-width': 0.5,
          'circle-stroke-color': '#ffffff',
        },
        layout: {
          visibility: 'none',
        },
      });
      // eslint-disable-next-line no-console
      // Rendering circle-based incident features.
    }
  } catch (err) {
    console.error('Incidents: error adding layer', err);
  }
}

/**
 * Legacy function for backward compatibility
 * Fetches incidents and adds them to the map
 * @deprecated Use fetchIncidents + addIncidentsLayer instead
 */
export function addIncidents(
  map: mapboxgl.Map | null,
  getToken: () => Promise<string>,
  setLoading?: (loading: boolean) => void,
): void {
  if (setLoading) setLoading(true);

  fetchIncidents(getToken)
    .then((incidents) => {
      addIncidentsLayer(map, incidents);
      if (setLoading) setLoading(false);
    })
    .catch((error) => {
      console.error('Failed to load incidents:', error);
      if (setLoading) setLoading(false);
    });
}
